import { useEffect, useState } from "react";
import { View, TouchableOpacity, StyleSheet, FlatList } from "react-native";
import { AuditEvent, ConnectorType } from "@axio/graphql/generated/graphql";
import CustomSearchInput from "../../../common/general/CustomSearchInput/CustomSearchInput";
import CustomText from "../../../common/general/CustomText/CustomText";
import { Info, CircleX, TriangleAlert } from "lucide-react";
import { formatDate, formatSubmittedTime } from "../../../../utils/date";
import CustomDropdown from "../../../common/general/CustomDropDown/CustomDropdown";

type AuditEventLevel = "info" | "warning" | "error";

const ITEMS_PER_PAGE = 8;

const LEVEL_CONFIG: Record<AuditEventLevel, { label: string; icon: JSX.Element; textColor: string; bgColor: string }> =
  {
    info: { label: "Info", icon: <Info size={14} color="#075985" />, textColor: "#075985", bgColor: "#E0F2FE" },
    warning: {
      label: "Warning",
      icon: <TriangleAlert size={14} color="#B54708" />,
      textColor: "#B54708",
      bgColor: "#FFFAEB",
    },
    error: { label: "Error", icon: <CircleX size={14} color="#B91C1C" />, textColor: "#B91C1C", bgColor: "#FEF2F2" },
  };

const formatConnectorName = (value: string) => {
  return value
    .toLowerCase()
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};
const formatAction = (type: string): string => {
  const action = type.replace(/^(connector|ingestion)_/, "");
  return action
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};
const CONNECTOR_TYPES = [ConnectorType.GoogleDrive, ConnectorType.Kaltura];
const CONNECTOR_OPTIONS = [
  { value: "all", label: "All connectors" },
  ...CONNECTOR_TYPES.map((name) => ({ value: name, label: formatConnectorName(name) })),
];

const getEventLevel = (type: string): AuditEventLevel => {
  if (type.endsWith("_failed")) return "error";
  if (type.endsWith("_expired")) return "warning";
  return "info";
};
type AuditLogTabProps = {
  events: AuditEvent[];
  search: string;
  connectorFilter: string;
  onSearchChange: (value: string) => void;
  onConnectorFilterChange: (value: string) => void;
};

export function AuditLogTab({
  events,
  search,
  connectorFilter,
  onSearchChange,
  onConnectorFilterChange,
}: AuditLogTabProps) {
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setCurrentPage(1);
  }, [connectorFilter, search]);

  const totalPages = Math.max(1, Math.ceil(events.length / ITEMS_PER_PAGE));

  const paged = events.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);

  return (
    <View style={styles.container}>
      <View style={styles.filtersRow}>
        <View style={styles.searchWrapper}>
          <CustomSearchInput
            minWidth={224}
            placeholder="Search..."
            searchQuery={search}
            setSearchQuery={onSearchChange}
            containerStyle={styles.searchContainer}
          />
        </View>

        <View style={styles.sortContainer}>
          <CustomDropdown
            options={CONNECTOR_OPTIONS}
            value={connectorFilter}
            onChange={onConnectorFilterChange}
            fullWidth={false}
            dropdownStyle={{ maxHeight: 40 }}
          />
        </View>
        <CustomText
          text={`${events.length} event${events.length !== 1 ? "s" : ""}`}
          role="text"
          useTranslationText={false}
          style={styles.eventCount}
        />
      </View>

      <View style={styles.tableCard}>
        <View style={styles.tableHeader}>
          <View style={styles.colLevel} />
          <CustomText
            text="Timestamp"
            role="text"
            useTranslationText={false}
            style={[styles.headerCell, styles.colTimestamp]}
          />
          <CustomText
            text="Actor"
            role="text"
            useTranslationText={false}
            style={[styles.headerCell, styles.colActor]}
          />
          <CustomText
            text="Action"
            role="text"
            useTranslationText={false}
            style={[styles.headerCell, styles.colAction]}
          />
          <CustomText
            text="Connector"
            role="text"
            useTranslationText={false}
            style={[styles.headerCell, styles.colConnector]}
          />
          <CustomText
            text="Details"
            role="text"
            useTranslationText={false}
            style={[styles.headerCell, styles.colDetails]}
          />
        </View>

        {paged.length === 0 ? (
          <View style={styles.emptyState}>
            <CustomText
              text="No events match your filters."
              role="text"
              useTranslationText={false}
              style={styles.emptyStateText}
            />
          </View>
        ) : (
          <FlatList
            data={paged}
            keyExtractor={(item) => item.id}
            scrollEnabled={false}
            renderItem={({ item, index }) => {
              const level = getEventLevel(item.type);
              const cfg = LEVEL_CONFIG[level];
              const isLast = index === paged.length - 1;

              return (
                <View style={[styles.tableRow, isLast && styles.tableRowLast]}>
                  <View style={styles.colLevel}>
                    <View style={[styles.levelBadge, { backgroundColor: cfg.bgColor }]}>{cfg.icon}</View>
                  </View>
                  <CustomText
                    text={`${formatDate(item.occurredAt)} - ${formatSubmittedTime(item.occurredAt)}`}
                    role="text"
                    useTranslationText={false}
                    style={[styles.cell, styles.colTimestamp, styles.cellSecondary]}
                    numberOfLines={1}
                  />
                  <CustomText
                    text={item?.actor?.name}
                    role="text"
                    useTranslationText={false}
                    style={[
                      styles.cell,
                      styles.colActor,
                      item.actor?.name === "System" ? styles.cellMuted : styles.cellPrimary,
                      styles.fontMedium,
                    ]}
                    numberOfLines={1}
                  />
                  <CustomText
                    text={formatAction(item.type)}
                    role="text"
                    useTranslationText={false}
                    style={[styles.cell, styles.colAction, styles.cellPrimary, styles.fontMedium]}
                    numberOfLines={1}
                  />
                  <CustomText
                    text={formatConnectorName(item.connectorType ?? "—")}
                    role="text"
                    useTranslationText={false}
                    style={[styles.cell, styles.colConnector, styles.cellSecondary]}
                    numberOfLines={1}
                  />
                  <CustomText
                    text={item.errorContext ?? "—"}
                    role="text"
                    useTranslationText={false}
                    style={[styles.cell, styles.colDetails, styles.cellSecondary]}
                    numberOfLines={1}
                  />
                </View>
              );
            }}
          />
        )}
      </View>

      {totalPages > 1 && (
        <View style={styles.pagination}>
          <CustomText
            text={`Page ${currentPage} of ${totalPages}`}
            role="text"
            useTranslationText={false}
            style={styles.pageLabel}
          />
          <View style={styles.pageButtons}>
            <TouchableOpacity
              style={[styles.pageBtn, currentPage === 1 && styles.pageBtnDisabled]}
              onPress={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              accessibilityLabel="Previous page">
              <CustomText text="‹" role="text" useTranslationText={false} style={styles.pageBtnText} />
            </TouchableOpacity>
            <TouchableOpacity
              style={[styles.pageBtn, currentPage === totalPages && styles.pageBtnDisabled]}
              onPress={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              accessibilityLabel="Next page">
              <CustomText text="›" role="text" useTranslationText={false} style={styles.pageBtnText} />
            </TouchableOpacity>
          </View>
        </View>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    gap: 12,
  },

  filtersRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 8,
    zIndex: 10,
  },
  searchWrapper: {
    width: 224,
  },
  searchContainer: {
    maxHeight: 40,
  },

  sortContainer: {
    zIndex: 5002,
    elevation: 5002,
  },

  eventCount: {
    fontSize: 12,
    color: "#667085",
    textAlign: "right",
    marginLeft: "auto",
  },

  tableCard: {
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 10,
    overflow: "hidden",
  },

  tableHeader: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#F9FAFB",
    borderBottomWidth: 1,
    borderBottomColor: "#D1D5DB",
  },
  headerCell: {
    fontSize: 12,
    fontWeight: "500",
    color: "#667085",
    letterSpacing: 0.4,
    paddingVertical: 12,
    paddingHorizontal: 16,
  },

  tableRow: {
    flexDirection: "row",
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#F3F4F6",
  },
  tableRowLast: {
    borderBottomWidth: 0,
  },
  cell: {
    fontSize: 14,
    fontWeight: "400",
    paddingVertical: 12,
    paddingHorizontal: 16,
  },
  cellPrimary: {
    color: "#101828",
    fontWeight: "400",
  },
  cellSecondary: {
    color: "#344054",
    fontWeight: "400",
  },
  cellMuted: {
    color: "#667085",
  },
  fontMedium: {
    fontWeight: "500",
  },

  colLevel: {
    width: 40,
    alignItems: "center",
  },
  colTimestamp: {
    // width: 120,
    flex: 1,
  },
  colActor: {
    flex: 1,
  },
  colAction: {
    flex: 1,
  },
  colConnector: {
    flex: 1,
  },

  colDetails: {
    flex: 2,
  },

  levelBadge: {
    width: 24,
    height: 24,
    borderRadius: 6,
    justifyContent: "center",
    alignItems: "center",
  },
  emptyState: {
    alignItems: "center",
    paddingVertical: 48,
  },
  emptyStateText: {
    fontSize: 14,
    color: "#667085",
  },

  pagination: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  pageLabel: {
    fontSize: 12,
    color: "#667085",
  },
  pageButtons: {
    flexDirection: "row",
    gap: 4,
  },
  pageBtn: {
    width: 32,
    height: 32,
    borderRadius: 8,
    backgroundColor: "#FFFFFF",
    borderWidth: 1,
    borderColor: "#E5E7EB",
    justifyContent: "center",
    alignItems: "center",
  },
  pageBtnDisabled: {
    opacity: 0.4,
  },
  pageBtnText: {
    fontSize: 18,
    color: "#344054",
    lineHeight: 22,
  },
});
